import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Briefcase, Plus, MapPin } from 'lucide-react';
import { atlasApi, AtlasUser, Vaga } from '../shared/atlasApi';
import Detalhe from './Detalhe';
import ModalNova from './ModalNova';
import MinhasIndicacoes from './MinhasIndicacoes';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

type View = 'lista' | 'detalhe' | 'indicacoes';

const STATUS: Record<string, { label: string; classes: string }> = {
  rascunho: { label: 'Rascunho', classes: 'bg-gray-100 text-gray-600' },
  aberta: { label: 'Aberta', classes: 'bg-emerald-100 text-emerald-700' },
  encerrada: { label: 'Encerrada', classes: 'bg-rose-100 text-rose-700' },
  preenchida: { label: 'Preenchida', classes: 'bg-blue-100 text-blue-700' },
};

const VagasApp: React.FC<Props> = ({ user, podeEditar }) => {
  const [view, setView] = useState<View>('lista');
  const [itens, setItens] = useState<Vaga[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [modal, setModal] = useState<{ aberto: boolean; editar: Vaga | null }>({ aberto: false, editar: null });

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.listarVagas(user)
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { if (view === 'lista') carregar(); }, [view, carregar]);

  if (view === 'detalhe' && selecionada) {
    return (
      <div className="p-6">
        <Detalhe user={user} vagaId={selecionada} podeEditar={podeEditar} onVoltar={() => { setView('lista'); setSelecionada(null); }} onEditar={(v) => setModal({ aberto: true, editar: v })} />
        {modal.aberto && <ModalNova user={user} vaga={modal.editar} onClose={() => setModal({ aberto: false, editar: null })} onSalvo={() => setModal({ aberto: false, editar: null })} />}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center"><Briefcase className="w-5 h-5" /></div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900">Vagas</h2>
          <p className="text-sm text-gray-500">Oportunidades internas e recrutamento.</p>
        </div>
        {podeEditar && (
          <button onClick={() => setModal({ aberto: true, editar: null })} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nova vaga
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {(['lista', 'indicacoes'] as const).map(v => (
          <button key={v} onClick={() => setView(v)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${view === v ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {v === 'lista' ? 'Vagas abertas' : 'Minhas indicações'}
          </button>
        ))}
      </div>

      {view === 'indicacoes' ? (
        <MinhasIndicacoes user={user} />
      ) : (
        <>
          {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}
          {loading ? (
            <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
          ) : itens.length === 0 ? (
            <div className="p-12 text-center text-gray-400 bg-white rounded-2xl border border-gray-200">Nenhuma vaga no momento.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {itens.map(v => {
                const st = STATUS[v.status] || { label: v.status, classes: 'bg-gray-100 text-gray-500' };
                return (
                  <button key={v._id} onClick={() => { setSelecionada(v._id); setView('detalhe'); }} className="text-left bg-white rounded-2xl border border-gray-200 p-5 hover:border-purple-300 hover:shadow-md transition">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 flex-1">{v.titulo}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${st.classes}`}>{st.label}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-2">
                      <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {v.tipo_contrato?.toUpperCase()}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {v.modalidade}</span>
                      {v.salario_combinar ? <span>A combinar</span> : (v.salario_min ? <span>A partir de R$ {v.salario_min}</span> : null)}
                    </div>
                    {v.descricao && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{v.descricao}</p>}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {modal.aberto && view !== 'detalhe' && (
        <ModalNova user={user} vaga={modal.editar} onClose={() => setModal({ aberto: false, editar: null })} onSalvo={() => { setModal({ aberto: false, editar: null }); carregar(); }} />
      )}
    </div>
  );
};

export default VagasApp;
